import { useMutation, useQueryClient } from '@tanstack/react-query';

import { dismissAllTasks, taskQueries } from '../api/task';
import { isActiveTaskStatus } from '../constants';
import useTaskApiInstance from './useTaskApiInstance';

import type { AxiosError } from 'axios';
import type { UseMutationOptions } from '@tanstack/react-query';
import type { TaskDismissAllResponse, TaskListResponse } from '../constants';

type UseDismissAllTasksOptions = UseMutationOptions<TaskDismissAllResponse, AxiosError, void>;

/**
 * 표시 가능한 Task 전체 숨김 처리 훅 ("성공/실패 태스크 제거" 버튼).
 * @description
 * - 성공하면 `taskQueries.all()`을 invalidate 해서 list를 최신화.
 */
const useDismissAllTasks = (options?: UseDismissAllTasksOptions) => {
  const queryClient = useQueryClient();
  const { instance } = useTaskApiInstance();

  return useMutation({
    mutationFn: () => dismissAllTasks(instance),
    ...options,
    onMutate: async variables => {
      const queryKey = taskQueries.lists();

      await queryClient.cancelQueries({ queryKey });

      // 진행 중(active) 태스크는 서버 dismiss-all 대상이 아니므로 옵티미스틱 캐시에서도 남긴다.
      queryClient.setQueriesData<TaskListResponse>({ queryKey }, old => {
        if (!old) return old;

        const remaining = old.data.filter(task => isActiveTaskStatus(task.statusType));

        return {
          ...old,
          data: remaining,
          hasMore: false,
          totalCount: remaining.length,
        };
      });

      await options?.onMutate?.(variables);
    },
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries({ queryKey: taskQueries.all() });
      options?.onSuccess?.(data, variables, context);
    },
  });
};

export default useDismissAllTasks;
